import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSchools, useDeleteSchool } from '@/api/hooks'
import { useDebounce, useSessionState, useScrollRestoration } from '@/shared/hooks'
import {
  Search, Plus, Download, Calendar, Phone, Mail,
  ChevronLeft, ChevronRight, Eye, Users,
  Percent, X, Trash2,
} from 'lucide-react'
import { cn } from '@/shared/utils'
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog'
import { toast } from '@/store/useToastStore'
import { Can } from '@/shared/ui/Can'

// ── Helpers ──────────────────────────────────────────────────────────────────
function getSchoolName(school: any): string {
  const tr = school?.vendor_translations?.find((t: any) => t.locale?.toLowerCase() === 'en')
    ?? school?.vendor_translations?.[0]
  return tr?.name ?? school?.name ?? 'Unknown School'
}

function getOwnerName(school: any): string {
  return school?.vendor_user?.user?.name ?? school?.owner_user?.name ?? school?.owner_name ?? '—'
}

function getOwnerEmail(school: any): string {
  return school?.vendor_user?.user?.email ?? school?.owner_user?.email ?? school?.owner_email ?? '—'
}

function formatDate(val?: string | null): string {
  if (!val) return '—'
  try {
    return new Date(val).toLocaleDateString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    }).replace(/\//g, '-')
  } catch { return val }
}

// ── Status Badge ──────────────────────────────────────────────────────────────
function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span className={cn(
      'px-3 py-0.5 rounded-full text-xs font-bold flex-shrink-0',
      isActive ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
    )}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  )
}

// ── Single stat block ─────────────────────────────────────────────────────────
function StatItem({ label, value, sub }: {
  label: string; value?: React.ReactNode; sub?: React.ReactNode
}) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-sm font-bold text-gray-900">{value ?? '—'}</span>
        {sub && <span className="text-[12px] text-red-500 font-semibold">{sub}</span>}
      </div>
      <div className="text-[12px] text-gray-400 leading-tight">{label}</div>
    </div>
  )
}

// ── School Card ───────────────────────────────────────────────────────────────
function SchoolCard({ school, onView, onDelete }: { school: any; onView: () => void; onDelete: () => void }) {
  const name    = getSchoolName(school)
  const email   = school.email ?? '—'
  const phone   = school.phone_number ?? school.phone ?? '—'
  const regDate = formatDate(school.created_at)
  // `is_active` is the authoritative flag — it's what the status filter above
  // actually queries by. The API's `status` string ("active"/"inactive") can
  // disagree with it, so reading `status` here could show "Active" for a
  // school the filter itself would classify as inactive.
  const isActive = !!school.is_active
  const ownerName  = getOwnerName(school)
  const ownerEmail = getOwnerEmail(school)
  const logo    = school.logo ?? null
  const initials = name.slice(0, 2).toUpperCase()
  const schoolId = school.id ? `#SC${String(school.id).padStart(4, '0')}` : '#SC0000'

  const totalTrainer  = school.total_trainers  ?? school.trainers_count  ?? 0
  const totalStudent  = school.total_students  ?? school.students_count  ?? 0
  const totalBookings = school.total_bookings ?? school.bookings_count  ?? 0
  const totalRevenue  = school.total_revenue  ?? school.revenue         ?? 0
  const totalCert     = school.total_certificates ?? school.certificates_count ?? 0
  const commission    = school.certificate_commission_in_percentage ?? 0

  const cardBg = isActive ? 'bg-green-50/40' : 'bg-red-50/30'

  return (
    <div className={cn('rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow', cardBg)}>

      {/* ── Header: logo + name + badge ── */}
      <div className="flex items-start justify-between px-4 pt-4 pb-2 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {logo ? (
            <img src={logo} alt={name}
              className="w-10 h-10 rounded-xl object-cover flex-shrink-0 border border-white shadow-sm"/>
          ) : (
            <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center text-brand-700 text-sm font-black flex-shrink-0 border border-white shadow-sm">
              {initials}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate">{name}</div>
            <div className="text-[12px] text-gray-400 font-medium">ID: {schoolId}</div>
          </div>
        </div>
        <StatusBadge isActive={isActive}/>
      </div>

      {/* ── Contact info ── */}
      <div className="px-4 pb-2 flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5 text-[13px] text-gray-500">
          <Mail size={10} className="text-gray-400 flex-shrink-0"/><span className="truncate">{email}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[13px] text-gray-500">
          <Phone size={10} className="text-gray-400 flex-shrink-0"/><span>{phone}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[13px] text-gray-500">
          <Calendar size={10} className="text-gray-400 flex-shrink-0"/>
          <span>Registered At: {regDate}</span>
        </div>
      </div>

      {/* ── School Owner chip ── */}
      <div className="mx-4 mb-3 rounded-xl bg-white/70 border border-gray-100 px-3 py-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-[12px] font-bold text-gray-600 flex-shrink-0">
            {ownerName.slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-[11px] text-gray-400">School Owner</div>
            <div className="text-xs font-semibold text-gray-800 truncate">{ownerName}</div>
          </div>
        </div>
        <span className="text-[13px] text-gray-400 truncate max-w-[120px]">{ownerEmail}</span>
      </div>

      {/* ── Stats row 1 ── */}
      <div className="px-4 pb-2 grid grid-cols-3 gap-3">
        <StatItem label="Total Trainer" value={totalTrainer}/>
        <StatItem label="Total Student" value={totalStudent}/>
        <StatItem label="Total Bookings" value={totalBookings}/>
      </div>

      {/* ── Stats row 2 ── */}
      <div className="px-4 pb-3 grid grid-cols-3 gap-3">
        <StatItem label="Total Revenue" value={Number(totalRevenue).toLocaleString()}/>
        <StatItem label="Total Certificate" value={totalCert}/>
        <StatItem label="Commission In (%)" value={commission}/>
      </div>

      {/* ── View Details + Delete CTA ── */}
      <div className="px-4 pb-4 flex items-center gap-2">
        <button
          onClick={onView}
          className="flex-1 py-2 rounded-xl border border-brand-300 text-brand-600 text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-brand-50 transition-colors"
        >
          <Eye size={12}/> View Details
        </button>
        <Can perm="DELETE /admin/vendor/*">
        <button
          onClick={onDelete}
          title="Delete school"
          className="px-3 py-2 rounded-xl border border-red-200 text-red-500 text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-red-50 transition-colors flex-shrink-0"
        >
          <Trash2 size={13}/> Delete
        </button>
        </Can>
      </div>
    </div>
  )
}

// ── Skeleton loading card ─────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden animate-pulse">
      <div className="px-4 pt-4 pb-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gray-200 flex-shrink-0"/>
        <div className="flex-1 space-y-1.5">
          <div className="h-3 bg-gray-200 rounded w-2/3"/>
          <div className="h-2 bg-gray-100 rounded w-1/3"/>
        </div>
        <div className="h-5 w-14 rounded-full bg-gray-200"/>
      </div>
      <div className="px-4 pb-3 space-y-1.5">
        <div className="h-2 bg-gray-100 rounded w-3/4"/>
        <div className="h-2 bg-gray-100 rounded w-1/2"/>
        <div className="h-2 bg-gray-100 rounded w-2/3"/>
      </div>
      <div className="mx-4 mb-3 h-10 rounded-xl bg-gray-100"/>
      <div className="px-4 pb-2 grid grid-cols-3 gap-3">
        {[1,2,3].map(i => <div key={i} className="h-10 bg-gray-100 rounded-lg"/>)}
      </div>
      <div className="px-4 pb-3 grid grid-cols-3 gap-3">
        {[1,2,3].map(i => <div key={i} className="h-10 bg-gray-100 rounded-lg"/>)}
      </div>
      <div className="px-4 pb-4">
        <div className="h-8 bg-gray-100 rounded-xl"/>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export function SchoolListPage() {
  const navigate = useNavigate()

  const [search,       setSearch]  = useState('')
  const [statusFilter, setStatus]  = useState('')   // '1' | '0' | ''
  const [commMin,      setCommMin] = useState('')   // commission % range
  const [commMax,      setCommMax] = useState('')
  const [dateFrom,     setDateFrom] = useState('')  // registered date range
  const [dateTo,       setDateTo]   = useState('')
  const [page,    setPage]    = useSessionState('school-list:page', 1)
  const [perPage, setPerPage] = useSessionState('school-list:perPage', 20)

  const debouncedSearch = useDebounce(search)
  const debCommMin = useDebounce(commMin)
  const debCommMax = useDebounce(commMax)

  const { data, isLoading, isError } = useSchools({
    page,
    limit:     perPage,
    search:    debouncedSearch || undefined,
    is_active: statusFilter !== '' ? Number(statusFilter) : undefined,
    min_commission: debCommMin !== '' ? Number(debCommMin) : undefined,
    max_commission: debCommMax !== '' ? Number(debCommMax) : undefined,
    start_date: dateFrom || undefined,
    end_date:   dateTo || undefined,
  })

  // Response: { status, message, data: { data: [ { user, vendor, ... } ], total, last_page, ... } }
  // Older/flat shapes (array directly under `data`) are still handled.
  useScrollRestoration('school-list', !isLoading)

  const payload: any = (data as any)?.data
  const rawRows: any[] = Array.isArray(payload) ? payload : (payload?.data ?? [])
  const total    = payload?.total ?? (data as any)?.meta?.total ?? (data as any)?.total ?? rawRows.length
  const lastPage = payload?.last_page ?? (Math.ceil(total / perPage) || 1)

  // Each row wraps the school under `vendor` with the school-admin under `user`.
  // Flatten to the vendor object so SchoolCard's accessors resolve, keeping the
  // owner + registration date that only exist on the wrapper.
  const rows: any[] = rawRows.map((r: any) =>
    r?.vendor
      ? { ...r.vendor, created_at: r.vendor.created_at ?? r.user?.created_at, owner_user: r.user }
      : r,
  )

  // all filtering is server-side; render rows as returned
  const filtered = rows

  const activeFilterCount = [statusFilter, commMin, commMax, dateFrom, dateTo].filter(Boolean).length

  const resetFilters = () => {
    setStatus(''); setCommMin(''); setCommMax(''); setDateFrom(''); setDateTo(''); setPage(1)
  }

  const handleView = useCallback((id: number) => {
    navigate(`/dashboard/school/${id}`)
  }, [navigate])

  // ── Delete ──────────────────────────────────────────────────────────────────
  const deleteSchool = useDeleteSchool()
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null)

  const confirmDelete = () => {
    if (!deleteTarget) return
    deleteSchool.mutate(deleteTarget.id, {
      onSuccess: (res: any) => {
        toast.success(res?.message ?? 'School deleted successfully')
        setDeleteTarget(null)
      },
      onError: (e: any) => {
        toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to delete school')
      },
    })
  }

  const handleExport = () => {
    const csv = [
      ['ID', 'Name', 'Email', 'Phone', 'Status', 'Created'],
      ...rows.map((s: any) => [
        s.id, getSchoolName(s), s.email ?? '', s.phone_number ?? s.phone ?? '',
        s.is_active ? 'Active' : 'Inactive', formatDate(s.created_at),
      ]),
    ].map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url  = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'schools.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  // page window helper
  const pageNumbers = (() => {
    let start = Math.max(1, page - 2)
    if (start + 4 > lastPage) start = Math.max(1, lastPage - 4)
    return Array.from({ length: Math.min(5, lastPage) }, (_, i) => start + i).filter(p => p <= lastPage)
  })()

  return (
    <div className="space-y-5">

      {/* ── Breadcrumb + Title ── */}
      <div>
        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-1">
          <span>Dashboard</span><span>/</span>
          <span className="text-brand-500 font-semibold">School</span>
        </div>
        <h1 className="text-xl font-bold text-gray-900">School</h1>
      </div>

      {/* ── Toolbar card ── */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-bold text-gray-800">School List</h2>

          <div className="flex items-center gap-2 flex-wrap">

            {/* Download */}
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-semibold transition-colors"
            >
              <Download size={12}/> Download
            </button>

            {/* Registered date range */}
            <div className="flex items-center gap-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-500">
              <Calendar size={12} className="text-gray-400 flex-shrink-0"/>
              <input
                type="date"
                value={dateFrom}
                onChange={e => { setDateFrom(e.target.value); setPage(1) }}
                className="text-xs text-gray-600 focus:outline-none bg-transparent w-[110px]"
              />
              <span className="text-gray-300">–</span>
              <input
                type="date"
                value={dateTo}
                onChange={e => { setDateTo(e.target.value); setPage(1) }}
                className="text-xs text-gray-600 focus:outline-none bg-transparent w-[110px]"
              />
            </div>

            {/* Commission % range */}
            <div className="flex items-center gap-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-500">
              <Percent size={12} className="text-gray-400 flex-shrink-0"/>
              <input
                type="number"
                min={0}
                value={commMin}
                onChange={e => { setCommMin(e.target.value); setPage(1) }}
                placeholder="Min"
                className="text-xs text-gray-600 placeholder-gray-400 focus:outline-none bg-transparent w-12"
              />
              <span className="text-gray-300">–</span>
              <input
                type="number"
                min={0}
                value={commMax}
                onChange={e => { setCommMax(e.target.value); setPage(1) }}
                placeholder="Max"
                className="text-xs text-gray-600 placeholder-gray-400 focus:outline-none bg-transparent w-12"
              />
            </div>

            {/* Status */}
            <select
              value={statusFilter}
              onChange={e => { setStatus(e.target.value); setPage(1) }}
              className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-500 min-w-28"
            >
              <option value="">Select Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>

            {/* Search */}
            <div className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2 bg-white min-w-40">
              <Search size={12} className="text-gray-400 flex-shrink-0"/>
              <input
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1) }}
                placeholder="Search by name"
                className="text-xs text-gray-600 placeholder-gray-400 focus:outline-none flex-1 bg-transparent min-w-0"
              />
              {search && (
                <button onClick={() => setSearch('')} className="text-gray-300 hover:text-gray-500">
                  <X size={11}/>
                </button>
              )}
            </div>

            {/* Reset */}
            {activeFilterCount > 0 && (
              <button
                onClick={resetFilters}
                className="flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
              >
                <X size={11}/> Reset ({activeFilterCount})
              </button>
            )}

            {/* Register New School */}
            <Can perm="POST /admin/vendor/*">
            <button
              onClick={() => navigate('/dashboard/school/new')}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-xs font-bold transition-colors"
            >
              <Plus size={13}/> Register New School
            </button>
            </Can>
          </div>
        </div>
      </div>

      {/* ── Error state ── */}
      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600">
          Failed to load schools. Check your API connection.
        </div>
      )}

      {/* ── Card grid ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i}/>)
          : filtered.length === 0
            ? (
              <div className="col-span-3 flex flex-col items-center justify-center py-20 text-center">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <Users size={24} className="text-gray-300"/>
                </div>
                <p className="text-sm font-semibold text-gray-500">No schools found</p>
                <p className="text-xs text-gray-400 mt-1">Try adjusting your filters or search term</p>
              </div>
            )
            : filtered.map((school: any) => (
              <SchoolCard
                key={school.id}
                school={school}
                onView={() => handleView(school.id)}
                onDelete={() => setDeleteTarget({ id: school.id, name: getSchoolName(school) })}
              />
            ))
        }
      </div>

      {/* ── Pagination ── */}
      {!isLoading && total > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 px-5 py-3 shadow-sm flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span>Items per page</span>
            <select
              value={perPage}
              onChange={e => { setPerPage(Number(e.target.value)); setPage(1) }}
              className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              {[10, 20, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>
              {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total} items
            </span>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14}/>
            </button>

            {pageNumbers.map(p => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={cn(
                  'w-8 h-8 flex items-center justify-center rounded-lg border text-xs font-semibold transition-colors',
                  p === page
                    ? 'bg-brand-500 text-white border-brand-500'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                )}
              >{p}</button>
            ))}

            <button
              onClick={() => setPage(p => Math.min(lastPage, p + 1))}
              disabled={page >= lastPage}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14}/>
            </button>

            <span className="ml-2 text-xs text-gray-400">of {lastPage}</span>
          </div>
        </div>
      )}

      {/* ── Delete confirmation ── */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete School"
        description={`Are you sure you want to delete "${deleteTarget?.name ?? ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteSchool.isPending}
      />
    </div>
  )
}
